/**
 * Transfer Policy — HR policy document HTML + print.
 * Version helpers shared with confirmation policy.
 */

export {
  clonePolicySnapshot,
  computeVersionOnSave,
  getPolicySnapshotsForDisplay,
  mergePolicySnapshots,
} from "./confirmationPolicyDocument";

import { buildApplicabilityProse } from "./confirmationPolicyDocument";
import {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./masters/companyDocumentDefaults";

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function yn(v) {
  if (v === true || v === "yes") return true;
  if (v === false || v === "no") return false;
  return null;
}

function createDocNumberer() {
  return {
    m: 0,
    s: 0,
    ss: 0,
    sss: 0,
    major() {
      this.m++;
      this.s = 0;
      this.ss = 0;
      this.sss = 0;
      return String(this.m);
    },
    sub() {
      this.s++;
      this.ss = 0;
      this.sss = 0;
      return `${this.m}.${this.s}`;
    },
    subsub() {
      this.ss++;
      this.sss = 0;
      return `${this.m}.${this.s}.${this.ss}`;
    },
    subsubsub() {
      this.sss++;
      return `${this.m}.${this.s}.${this.ss}.${this.sss}`;
    },
  };
}

const TRIGGER_LABELS = {
  approval: "Upon transfer approval",
  cancellation: "Upon transfer cancellation",
};

/**
 * @param {object} policy
 * @param {object} options — same as confirmation (companyName, branchesList, …)
 */
export function buildTransferPolicyDocumentHtml(policy, options = {}) {
  const p = policy || {};
  const org = options.companyName || MASTERS_DOCUMENT_COMPANY_NAME;
  const companyName = escapeHtml(org);
  const companyAddressRaw = options.companyAddress ?? MASTERS_DOCUMENT_COMPANY_ADDRESS;
  const preparedBy = options.preparedBy || "Human Resources";
  const approvedBy = options.approvedBy || "Management";
  const roleLabel =
    options.workflowRoleLabel ||
    ((role) => {
      const map = {
        manager: "Manager",
        hr: "HR",
        management: "Management",
        reporting_manager: "Reporting Manager",
      };
      return map[role] || role || "—";
    });

  const ver = p.policyVersion != null ? p.policyVersion : 1;
  const eff = escapeHtml(p.effectiveDate || "—");
  const policyTitle = escapeHtml(p.policyName || p.name || "Transfer policy");

  const wf = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
  const cancelWf = Array.isArray(p.cancellationWorkflowSteps) ? p.cancellationWorkflowSteps : [];
  const checklist = Array.isArray(p.checklistMappings) ? p.checklistMappings : [];

  const transferMasterLis = [
    ["Branch", !!p.transferrableBranch],
    ["Department", !!p.transferrableDepartment],
    ["Designation", !!p.transferrableDesignation],
    ["Sub-department / division", !!p.transferrableSubDepartment],
    ["Category", !!p.transferrableCategory],
    ["Grade", !!p.transferrableGrade],
    ["Reporting manager", !!p.transferrableReportingManager],
  ]
    .filter(([, on]) => on)
    .map(([label]) => `<li>${escapeHtml(label)}</li>`)
    .join("");
  const transferListHtml = transferMasterLis
    ? `<ul>${transferMasterLis}</ul>`
    : `<p>No master-data fields are enabled in this policy version — contact HR.</p>`;

  const approvalMethodProse =
    p.approvalMethod === "workflow"
      ? "Transfer requests under this policy follow the multi-step approval workflows configured below. Each step must be completed within the stated service levels unless escalated or varied by HR."
      : p.approvalMethod === "on_instance"
        ? "Approvals may be decided on a case-by-case basis. Where workflow steps apply to an individual transfer, the same order and timelines apply to that case."
        : "Eligible transfer requests may be processed when rules are satisfied, without a standing manual chain, subject to HR oversight.";

  const pushedNote =
    p.policyDocumentPushed && p.policyPushedAt
      ? `<p class="meta"><strong>Employee publication:</strong> This version was published to covered employees on ${escapeHtml(
          new Date(p.policyPushedAt).toLocaleString()
        )}.</p>`
      : "";

  const addressBlock = companyAddressRaw
    ? `<p class="addr">${escapeHtml(companyAddressRaw).replace(/\n/g, "<br/>")}</p>`
    : "";

  const docNum = createDocNumberer();

  const wfStepsHtml = (steps, titleWithoutNumber) => {
    const sec = docNum.sub();
    const fullTitle = `${sec} ${titleWithoutNumber}`;
    if (!steps.length) {
      return `<h3>${escapeHtml(fullTitle)}</h3><p>No separate approval steps are defined for this path in the current version. HR will apply the general approval approach and company practice.</p>`;
    }
    const items = steps
      .map((s) => {
        const stepNum = docNum.subsub();
        const detailNum = docNum.subsubsub();
        const r = roleLabel(s.role);
        const line = s.notifyOnly
          ? `This step is for <strong>notification only</strong> (no approval action required from ${escapeHtml(r)}).`
          : `${escapeHtml(r)} must complete the approval action within <strong>${s.slaDays ?? 3} calendar day(s)</strong> unless escalated.`;
        return `<li><p><strong>${stepNum} — ${escapeHtml(r)}.</strong></p><p class="step-detail"><strong>${detailNum}</strong> ${line}</p></li>`;
      })
      .join("");
    return `<h3>${escapeHtml(fullTitle)}</h3><ol class="steps">${items}</ol>`;
  };

  const renderChecklistSection = () => {
    const sec = docNum.major();
    if (!checklist.length) {
      return `<h2>${sec}. Checklist and handover</h2><p>No checklist tasks are listed in this version. HR may still assign IT, finance, or admin tasks under general procedures.</p>`;
    }
    const rows = checklist
      .map((item) => {
        const rowNum = docNum.sub();
        const trig = TRIGGER_LABELS[item.triggerEvent] || item.triggerEvent || "—";
        const due =
          item.dueDateLogic === "approval_date"
            ? `${item.dueDateDays ?? 7} day(s) from approval date`
            : item.dueDateLogic === "fixed_days"
              ? `${item.dueDateDays ?? 7} day(s) from trigger`
              : escapeHtml(item.dueDateLogic || "—");
        const depOn = item.dependsOnTaskId
          ? escapeHtml(checklist.find((x) => x.taskId === item.dependsOnTaskId)?.name || item.dependsOnTaskId)
          : "—";
        return `<tr>
          <td>${rowNum}</td>
          <td>${escapeHtml(trig)}</td>
          <td>${escapeHtml(item.department || "—")}</td>
          <td>${escapeHtml(item.actionRole || "—")}</td>
          <td>${escapeHtml(item.name || "—")}</td>
          <td>${item.mandatory ? "Mandatory" : "Optional"}</td>
          <td>${escapeHtml(due)}</td>
          <td>${depOn}</td>
        </tr>`;
      })
      .join("");
    return `
      <h2>${sec}. Checklist and handover</h2>
      <p>Tasks below may run when the stated trigger occurs. Mandatory items should be completed so the transfer can close and records update correctly.</p>
      <table class="grid">
        <thead>
          <tr><th>Ref.</th><th>Trigger</th><th>Dept</th><th>Role</th><th>Task</th><th>Nature</th><th>Due</th><th>Depends on</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`;
  };

  const alerts = p.alerts || {};
  const notificationsBlock =
    alerts.hr !== false || alerts.manager !== false || alerts.employee !== false
      ? `<p><strong>${docNum.sub()}.</strong> System notifications may be sent to: HR <strong>${alerts.hr !== false ? "yes" : "no"}</strong>; managers <strong>${alerts.manager !== false ? "yes" : "no"}</strong>; employee <strong>${alerts.employee !== false ? "yes" : "no"}</strong>.</p>`
      : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>${policyTitle} — ${companyName}</title>
  <style>
    body { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12.5px; line-height: 1.6; color: #1e293b; max-width: 820px; margin: 0 auto; padding: 32px; }
    .letterhead { border-bottom: 2px solid #0f172a; padding-bottom: 18px; margin-bottom: 24px; }
    .company { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 8px 0; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .doc-title { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 15px; font-weight: 700; color: #0f172a; margin: 16px 0 8px 0; text-transform: uppercase; letter-spacing: .06em; }
    .addr { margin: 0; color: #475569; font-size: 11px; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .control { margin-top: 14px; padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 11px; color: #334155; }
    .control strong { color: #0f172a; }
    p.meta { font-size: 11px; color: #64748b; margin-top: 10px; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    h2 { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 13px; margin: 22px 0 10px 0; color: #0f172a; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    h3 { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12px; margin: 14px 0 8px 0; color: #334155; }
    p { margin: 0 0 12px 0; text-align: justify; }
    ul { margin: 8px 0 12px 20px; }
    ol.steps { margin: 8px 0 16px 0; padding: 0; list-style-type: none; }
    ol.steps li { margin-bottom: 10px; }
    p.step-detail { margin: 4px 0 0 12px; font-size: 11.5px; color: #475569; }
    table.grid { width: 100%; border-collapse: collapse; font-size: 11px; margin: 10px 0 16px 0; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    table.grid th, table.grid td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; vertical-align: top; }
    table.grid th { background: #f1f5f9; font-weight: 600; color: #334155; }
    .sign { margin-top: 32px; display: grid; grid-template-columns: 1fr 1fr; gap: 28px; font-size: 11px; color: #475569; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .sign .line { border-top: 1px solid #94a3b8; padding-top: 8px; margin-top: 36px; color: #0f172a; font-weight: 600; }
    @media print { body { padding: 18px; } }
  </style>
</head>
<body>
  <div class="letterhead">
    <p class="company">${companyName}</p>
    ${addressBlock}
    <p class="doc-title">HR transfer policy</p>
    <p style='font-family:"Segoe UI",Inter,Arial,sans-serif;font-size:13px;margin:0 0 4px 0;'><strong>${policyTitle}</strong></p>
    <div class="control">
      <strong>Document version:</strong> ${escapeHtml(String(ver))} &nbsp;|&nbsp;
      <strong>Effective from:</strong> ${eff} &nbsp;|&nbsp;
      <strong>Owned by:</strong> ${escapeHtml(preparedBy)} / ${escapeHtml(approvedBy)}
    </div>
    ${pushedNote}
  </div>

  <h2>${docNum.major()}. Purpose</h2>
  <p><strong>${docNum.sub()}.</strong> This policy explains how <strong>${companyName}</strong> governs <strong>internal transfers</strong> of employees — including changes to branch, department, reporting line, or other master data where permitted — and how approvals, letters, checklists, and cancellations are handled in the HR system.</p>
  <p><strong>${docNum.sub()}.</strong> It applies together with your employment contract and applicable law. Case-specific decisions remain with management and HR.</p>

  <h2>${docNum.major()}. Applicability</h2>
  ${buildApplicabilityProse(p, { ...options, companyName: org })}

  <h2>${docNum.major()}. What may change in a transfer</h2>
  <p><strong>${docNum.sub()}.</strong> Subject to this policy and management approval, a transfer may allow updates to the following master data (only items enabled below):</p>
  ${transferListHtml}

  <h2>${docNum.major()}. Approvals and workflows</h2>
  <p>${approvalMethodProse}</p>
  ${
    p.approvalMethod === "workflow" || p.needWorkflowSteps
      ? `<p><strong>${docNum.sub()}.</strong> Reminder notifications may be sent <strong>${escapeHtml(String(p.reminderDays ?? 7))}</strong> calendar day(s) before the transfer effective date where configured. Parallel approval: <strong>${p.workflowParallelApproval ? "Enabled" : "Disabled"}</strong>. Escalation after <strong>${escapeHtml(String(p.workflowEscalationAfterDays ?? 3))}</strong> day(s) without action. Auto-approval if no action: <strong>${p.workflowAutoApprovalIfNoAction ? "Yes (where allowed)" : "No"}</strong>. Rejection with reason: <strong>${p.workflowRejectionWithReason !== false ? "Yes" : "No"}</strong>. Re-initiation: <strong>${p.workflowReinitiationAllowed !== false ? "Allowed" : "Restricted"}</strong>.</p>`
      : `<p><strong>${docNum.sub()}.</strong> Workflow parameters apply when workflow mode is active for a case.</p>`
  }
  ${wfStepsHtml(wf, "Transfer approval workflow")}
  ${wfStepsHtml(cancelWf, "Transfer cancellation workflow")}

  <h2>${docNum.major()}. Letters and templates</h2>
  <p><strong>${docNum.sub()}.</strong> Auto issue transfer letter: <strong>${p.autoLetter === "yes" ? "Yes" : "No"}</strong>. Letter format reference: <strong>${escapeHtml(p.letterFormat || "—")}</strong>. Transfer letter template: <strong>${escapeHtml(p.letterTransfer || "—")}</strong>. Cancellation letter template: <strong>${escapeHtml(p.letterCancellation || "—")}</strong>. Use cancellation letter assignment: <strong>${p.useCancellationLetter ? "Yes" : "No"}</strong>${p.useCancellationLetter ? `; format: <strong>${escapeHtml(p.cancellationLetterFormat || "—")}</strong>.` : "."}</p>

  ${renderChecklistSection()}

  <h2>${docNum.major()}. Notifications and assignment</h2>
  ${notificationsBlock || `<p><strong>${docNum.sub()}.</strong> Notification channels follow system defaults.</p>`}
  <p><strong>${docNum.sub()}.</strong> Auto-assign this policy on joining: <strong>${p.autoAssignOnJoin ? "Yes" : "No"}</strong> (for employees matching applicability).</p>

  <h2>${docNum.major()}. Disclaimer</h2>
  <p>This document summarises transfer rules for <strong>${companyName}</strong>. It is not a contract by itself. If you are unsure how it applies, contact HR.</p>

  <div class="sign">
    <div>
      <div class="line">Prepared by (${escapeHtml(preparedBy)})</div>
      <span>Name &amp; signature</span>
    </div>
    <div>
      <div class="line">Approved by (${escapeHtml(approvedBy)})</div>
      <span>Name &amp; signature</span>
    </div>
  </div>
</body>
</html>`;
}

export function printTransferPolicyDocumentHtml(htmlString) {
  const w = window.open("", "_blank");
  if (!w) {
    window.print();
    return;
  }
  w.document.write(htmlString);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 300);
}
